import React, { createContext, useState, useContext, ReactNode } from 'react';
import { translations } from '../utils/translations';

type Language = 'en' | 'zh';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: typeof translations.en;
    showLanguageModal: boolean;
    setShowLanguageModal: (show: boolean) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Check if this is first visit (no saved language)
    const isFirstVisit = !localStorage.getItem('appLanguage');

    // Try to get language from localStorage or default to 'en'
    const [language, setLanguageState] = useState<Language>(() => {
        const saved = localStorage.getItem('appLanguage');
        return (saved === 'en' || saved === 'zh') ? saved : 'en';
    });

    // Show modal on first visit only
    const [showLanguageModal, setShowLanguageModal] = useState(isFirstVisit);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        // Save to localStorage (web equivalent of AsyncStorage)
        localStorage.setItem('appLanguage', lang);
    };

    const t = translations[language];

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, showLanguageModal, setShowLanguageModal }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
